// src/utils/conditionEngine.js
import { CONDITION_TYPES } from './conditionTypes';

function matchesFilter(item, filter) {
  for (const key in filter) {
    if (item[key] !== filter[key]) return false;
  }
  return true;
}

function isInTimeRange(item, timeRange) {
  const start = item.start || item.time || '';
  return start >= timeRange.start && start <= timeRange.end;
}

function getEventCount(event, progress, filter, timeRange) {
  let items = progress.history[event] || [];
  if (filter) items = items.filter(item => matchesFilter(item, filter));
  if (timeRange) items = items.filter(item => isInTimeRange(item, timeRange));
  return items.length;
}

function getEventSum(event, metric, progress, timeRange) {
  let items = progress.history[event] || [];
  if (timeRange) items = items.filter(item => isInTimeRange(item, timeRange));
  return items.reduce((sum, item) => sum + (item[metric] || 0), 0);
}

function getCurrentStreak(event, progress, timeRange) {
  const items = progress.history[event] || [];
  let filtered = items;
  if (timeRange) filtered = items.filter(item => isInTimeRange(item, timeRange));
  const dates = [...new Set(filtered.map(item => item.date))].sort();
  if (dates.length === 0) return 0;
  let streak = 1;
  for (let i = dates.length - 1; i > 0; i--) {
    const prev = new Date(dates[i-1]);
    const curr = new Date(dates[i]);
    const diff = (curr - prev) / (1000*60*60*24);
    if (diff === 1) streak++;
    else break;
  }
  return streak;
}

function isEventDoneToday(event, progress) {
  const today = new Date().toISOString().split('T')[0];
  const items = progress.history[event] || [];
  return items.some(item => item.date === today);
}

export function checkCondition(condition, progress) {
  switch (condition.type) {
    case CONDITION_TYPES.COUNT: {
      const count = getEventCount(condition.event, progress, condition.filter, condition.timeRange);
      return {
        isMet: count >= condition.target,
        progress: Math.min(100, Math.round((count / condition.target) * 100))
      };
    }
    case CONDITION_TYPES.SUM: {
      const sum = getEventSum(condition.event, condition.metric, progress, condition.timeRange);
      return {
        isMet: sum >= condition.target,
        progress: Math.min(100, Math.round((sum / condition.target) * 100))
      };
    }
    case CONDITION_TYPES.STREAK: {
      const streak = getCurrentStreak(condition.event, progress, condition.timeRange);
      return {
        isMet: streak >= condition.target,
        progress: Math.min(100, Math.round((streak / condition.target) * 100))
      };
    }
    case CONDITION_TYPES.TIME_RANGE: {
      const count = getEventCount(condition.event, progress, condition.filter, condition.timeRange);
      return {
        isMet: count >= condition.target,
        progress: Math.min(100, Math.round((count / condition.target) * 100))
      };
    }
    case CONDITION_TYPES.DAILY: {
      const done = isEventDoneToday(condition.event, progress);
      return { isMet: done, progress: done ? 100 : 0 };
    }
    case CONDITION_TYPES.COMPOSITE: {
      const results = condition.conditions.map(c => checkCondition(c, progress));
      const allMet = condition.operator === 'AND' ? results.every(r => r.isMet) : results.some(r => r.isMet);
      const avgProgress = results.reduce((sum, r) => sum + r.progress, 0) / results.length;
      return { isMet: allMet, progress: Math.min(100, Math.round(avgProgress)) };
    }
    case CONDITION_TYPES.CUSTOM: {
      return condition.check(progress);
    }
    default:
      return { isMet: false, progress: 0 };
  }
}

export function createEmptyProgress() {
  return {
    unlocked: [],
    progress: {},
    counters: {},
    history: {},
    lastEventDate: {},
  };
}